import { json } from "@remix-run/node";
import type { ActionFunctionArgs, MetaFunction } from "@remix-run/node";
import { Form, Link, useActionData, useNavigation } from "@remix-run/react";
import { db } from "~/lib/db.server";
import { sendPasswordResetEmail } from "~/lib/email.server";
import { BloodhoundLogo } from "~/components/BloodhoundLogo";
import crypto from "crypto";

export const meta: MetaFunction = () => [
  { title: "Forgot password — Bloodhound" },
];

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData();
  const email = (form.get("email") as string)?.trim().toLowerCase();

  if (!email) {
    return json({ error: "Email is required.", sent: false }, { status: 400 });
  }

  const user = await db.workspaceUser.findFirst({ where: { email } });

  // Always return success to avoid user enumeration
  if (user) {
    const token = crypto.randomBytes(32).toString("hex");
    const expiresAt = new Date(Date.now() + 60 * 60 * 1000); // 1 hour

    await db.passwordResetToken.create({
      data: {
        id: crypto.randomUUID(),
        userId: user.id,
        token,
        expiresAt,
      },
    });

    const appUrl = process.env.APP_URL ?? "https://dashboard.getbloodhound.app";
    await sendPasswordResetEmail(email, `${appUrl}/reset-password/${token}`);
  }

  return json({ error: null, sent: true });
}

export default function ForgotPassword() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const submitting = navigation.state === "submitting";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#080808] relative overflow-hidden">
      <div className="radar-sweep" />
      <div className="radar-rings"><span /><span /><span /><span /></div>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 600px 500px at 50% 55%, rgba(220,38,38,0.04) 0%, transparent 70%)" }} />
      <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

      <div className="relative z-10 w-full max-w-sm px-6">
        <div className="flex flex-col items-center mb-10">
          <BloodhoundLogo size="lg" />
        </div>

        <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-7 shadow-2xl shadow-black/40 backdrop-blur-md">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center mb-2">
            Reset password
          </p>

          {actionData?.sent ? (
            <div className="text-center py-4">
              <p className="text-sm text-gray-300 leading-relaxed">
                If that email is registered, you'll receive a reset link shortly. Check your inbox.
              </p>
              <Link to="/login" className="mt-5 inline-block text-xs text-gray-400 hover:text-gray-300 underline underline-offset-2">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500 text-center mb-5 leading-relaxed">
                Enter your email and we'll send you a reset link.
              </p>
              <Form method="post" className="space-y-3">
                {actionData?.error && (
                  <p className="text-xs text-red-400 text-center bg-red-950/30 border border-red-800/30 rounded-lg px-3 py-2">
                    {actionData.error}
                  </p>
                )}
                <input
                  name="email"
                  type="email"
                  placeholder="Email"
                  autoComplete="email"
                  required
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#dc2626] text-white font-semibold text-sm px-4 py-3.5 rounded-xl hover:bg-[#b91c1c] transition-colors duration-150 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </Form>
              <p className="mt-4 text-center text-xs text-gray-500">
                <Link to="/login" className="text-gray-400 hover:text-gray-300 underline underline-offset-2">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
